import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useHousehold, useHouseholdDerived } from '../contexts/HouseholdContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import { useLiabilities } from '../contexts/LiabilityContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { usePortfolio } from '../contexts/PortfolioContext';
import { useSampleData } from '../contexts/SampleDataContext';
import type { SyncedPortfolioAccount } from '../services/portfolioSyncService';
import { getCategoryColor } from '../data/categoryColors';
import { deriveHousehold } from '../data/deriveHousehold';
import {
  getAnalyticsTrends,
  loadAnalyticsSummary,
} from '../services/analyticsService';
import {
  getDashboardRefreshGeneration,
  notifyDashboardRefresh,
  subscribeDashboardRefresh,
} from '../services/dashboardRefreshBus';
import type { AnalyticsCategoryRow } from '../types/analytics';
import type {
  CategorySpend,
  SampleAsset,
  SampleLiability,
  SampleNetWorth,
  SampleProfile,
  SampleProjections,
} from '../types/financial';
import type { OnboardingDraft } from '../types/onboarding';
import { ONBOARDING_STEP } from '../types/onboarding';
import {
  draftHasMeaningfulAssets,
  hasUserOwnedAccounts,
  isSampleAccountId,
  localLiabilitiesOnly,
  mergeDraftIntoHousehold,
  overlaySyncedPortfolioOnHousehold,
  stripSampleAccounts,
} from '../utils/householdFromDraft';
import { mergeHomeLoanIntoNetWorth } from '../utils/applyLoanToHousehold';
import { estimatePaymentSplit } from '../utils/loanAmortization';
import {
  isPortfolioInvestmentAccount,
  isSpendLikePortfolioAccount,
} from '../utils/portfolioAccountCategory';
import { pickPrimaryMortgage } from './useLoanSnapshot';
import { formatMonthKeyLabel, shiftMonthKey } from './useSpendBreakdown';

/** YYYY-MM month key for the Home spend snapshot. */
export type HomeSpendPeriod = string;

export function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Newest → oldest: this month and the two prior months. */
export function homeSpendLastThreeMonths(fromKey = currentMonthKey()): HomeSpendPeriod[] {
  return [0, -1, -2].map((delta) => shiftMonthKey(fromKey, delta));
}

export function homeSpendPeriodKey(period: HomeSpendPeriod): string {
  return period;
}

export function homeSpendPeriodLabel(period: HomeSpendPeriod): string {
  const thisMonth = currentMonthKey();
  if (period === thisMonth) return 'This month';
  if (period === shiftMonthKey(thisMonth, -1)) return 'Last month';
  return formatMonthKeyLabel(period);
}

/** Compact chip label — "Aug", "Jul", … */
export function homeSpendPeriodChipLabel(period: HomeSpendPeriod): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
}

export type DashboardSummaries = {
  totalSpend: number;
  totalSaved: number;
  categories: CategorySpend[];
  netCashFlow: number;
  isEmpty: boolean;
};

export type HomeDashboardData = {
  isLive: boolean;
  loading: boolean;
  error: string | null;
  profile: SampleProfile;
  summaries: DashboardSummaries;
  /** Month window used for the Home spend snapshot. */
  spendPeriod: HomeSpendPeriod;
  spendPeriodKey: string;
  setSpendPeriod: (period: HomeSpendPeriod) => void;
  netWorth: SampleNetWorth;
  mortgage: SampleLiability | null;
  projections: SampleProjections;
  usingDraftPreview: boolean;
  refresh: () => void;
};

/** Newest month key in `points` that has spend activity. */
function latestMonthWithSpend(
  points: Array<{ periodKey: string; totalAmount: number; transactionCount: number }>,
): string | null {
  const sorted = [...points].sort((a, b) => b.periodKey.localeCompare(a.periodKey));
  for (const point of sorted) {
    if (Math.abs(point.totalAmount) > 0 || point.transactionCount > 0) {
      return point.periodKey;
    }
  }
  return null;
}

function mapCategories(rows: AnalyticsCategoryRow[]): CategorySpend[] {
  return rows.map((row, index) => ({
    id: row.categorySlug,
    name: row.categoryName,
    amount: Math.abs(row.totalAmount),
    color: getCategoryColor(row.categorySlug, index),
  }));
}

function buildLiveProfile(userName?: string): SampleProfile {
  return {
    countryCode: 'US',
    currency: 'USD',
    annualSalary: 0,
    monthlyIncome: 0,
    savingsRateTarget: 0,
    householdName: userName ?? 'Your household',
  };
}

function emptyNetWorth(): SampleNetWorth {
  return {
    total: 0,
    activeInvestments: 0,
    passiveInvestments: 0,
    totalLiabilities: 0,
    assets: [],
    liabilities: [],
  };
}

function emptyProjections(startYear = new Date().getUTCFullYear()): SampleProjections {
  return {
    crossoverYear: 0,
    netWorthIn10Years: 0,
    mortgageBalanceSeries: [{ year: startYear, balance: 0 }],
    debtBalanceSeries: [{ year: startYear, balance: 0 }],
    investmentBalanceSeries: [{ year: startYear, balance: 0 }],
    netWorthByYear: [],
  };
}

function draftHasCustomIncome(draft: OnboardingDraft): boolean {
  if (!draft.completedIntro || draft.monthlyIncomeAfterTax <= 0) {
    return false;
  }
  // Only treat income as a preview once the user has moved past the income step,
  // or has entered assets/liabilities (avoids replacing sample with catalog mid).
  return draft.step > ONBOARDING_STEP.COUNTRY_INCOME || draftHasMeaningfulAssets(draft);
}

/**
 * Prefer Mongo-synced portfolio rows for Active investments + Other assets.
 * When a snapshot exists, assets are network-only — no local/sample/draft mix-in.
 */
function applySyncedPortfolio(
  netWorth: SampleNetWorth,
  accounts: SyncedPortfolioAccount[],
  hasSnapshot: boolean,
): SampleNetWorth {
  // No Mongo snapshot yet → keep local / draft balance sheet.
  if (!hasSnapshot) return netWorth;

  const holdings = accounts.filter((account) => !isSpendLikePortfolioAccount(account));
  const investments: SampleAsset[] = holdings
    .filter((account) => isPortfolioInvestmentAccount(account))
    .map((account, index) => ({
      id: account.accountId?.trim() || `synced_invest_${index}`,
      type: account.type || 'investment',
      label: account.label,
      value: account.balance,
      isInvestment: true,
      expectedReturnPct: account.expectedReturnPct,
    }));

  const otherAssets: SampleAsset[] = holdings
    .filter((account) => !isPortfolioInvestmentAccount(account))
    .map((account, index) => ({
      id: account.accountId?.trim() || `synced_other_${index}`,
      type: account.type || 'other',
      label: account.label,
      value: account.balance,
      isInvestment: false,
    }));

  const assets = [...investments, ...otherAssets];
  const activeInvestments = investments.reduce((sum, a) => sum + a.value, 0);
  const passiveInvestments = otherAssets.reduce((sum, a) => sum + a.value, 0);

  return {
    ...netWorth,
    assets,
    activeInvestments,
    passiveInvestments,
    total: activeInvestments + passiveInvestments - netWorth.totalLiabilities,
  };
}

function netWorthFromDraft(draft: OnboardingDraft, fallback: SampleNetWorth): SampleNetWorth {
  if (!draftHasMeaningfulAssets(draft)) {
    return fallback;
  }

  const draftAssets: SampleAsset[] = draft.assets
    .filter((asset) => asset.value > 0)
    .map((asset, index) => ({
      id: `draft_asset_${asset.type}_${index}`,
      type: asset.type,
      label: asset.label,
      value: asset.value,
      isInvestment: asset.isInvestment,
      expectedReturnPct: asset.expectedReturnPct,
    }));

  const draftLiabilities: SampleLiability[] = draft.liabilities
    .filter((liability) => liability.balance > 0)
    .map((liability, index) => {
      const split =
        liability.monthlyInterest != null && liability.monthlyPrincipal != null
          ? {
              monthlyInterest: liability.monthlyInterest,
              monthlyPrincipal: liability.monthlyPrincipal,
            }
          : liability.interestRatePct != null && liability.monthlyPayment != null
            ? estimatePaymentSplit(
                liability.balance,
                liability.interestRatePct,
                liability.monthlyPayment,
              )
            : {};

      return {
        id: `draft_liab_${liability.type}_${index}`,
        type: liability.type,
        label: liability.label,
        balance: liability.balance,
        interestRatePct: liability.interestRatePct,
        monthlyPayment: liability.monthlyPayment,
        ...split,
      };
    });

  // Keep draft investment labels exact (TFSA, FK, …). Do not mix in sample 401(k)/Roth rows.
  const assets = [...draftAssets];
  for (const asset of fallback.assets) {
    if (asset.isInvestment) continue;
    if (asset.id.startsWith('asset_')) continue;
    const key = asset.label.trim().toLowerCase();
    if (!assets.some((a) => a.label.trim().toLowerCase() === key)) {
      assets.push(asset);
    }
  }

  // Keep household / sample loans (liab_mortgage, auto, cards) unless the
  // draft actually entered its own liability rows. Dropping `liab_*` here
  // made Home "Liabilities" $0 while the loan widget still showed a balance.
  const liabilities =
    draftLiabilities.length > 0 ? draftLiabilities : fallback.liabilities;

  const activeInvestments = assets
    .filter((a) => a.isInvestment)
    .reduce((sum, a) => sum + a.value, 0);
  const passiveInvestments = assets
    .filter((a) => !a.isInvestment)
    .reduce((sum, a) => sum + a.value, 0);
  const totalLiabilities = liabilities.reduce((sum, l) => sum + l.balance, 0);

  return {
    total: activeInvestments + passiveInvestments - totalLiabilities,
    activeInvestments,
    passiveInvestments,
    totalLiabilities,
    assets,
    liabilities,
  };
}

export function useHomeDashboard(): HomeDashboardData {
  const { isSampleMode } = useSampleData();
  const derived = useHouseholdDerived();
  const { core, setCore, refresh: refreshHousehold } = useHousehold();
  const { draft } = useOnboarding();
  const {
    accounts: syncedPortfolio,
    latest: syncedLatest,
    refresh: refreshPortfolio,
  } = usePortfolio();
  const {
    liabilityAccounts: syncedLiabilities,
    refresh: refreshLiabilities,
  } = useLiabilities();
  const hasSyncedSnapshot = syncedLatest != null;
  const hasSyncedLiabilities = syncedLiabilities.length > 0;
  const { isAuthenticated, tokens, user } = useAuth();
  const { view: householdView, viewReady } = useHouseholdMembership();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveSummaries, setLiveSummaries] = useState<DashboardSummaries | null>(null);
  const [spendPeriod, setSpendPeriodState] = useState<HomeSpendPeriod>(currentMonthKey);
  const [userPickedSpendPeriod, setUserPickedSpendPeriod] = useState(false);
  const [tick, setTick] = useState(0);
  const seenGenerationRef = useRef(0);
  const loadedPeriodKeyRef = useRef<string | null>(null);
  const hasLiveSummariesRef = useRef(false);
  const fallbackAttemptedRef = useRef(false);
  hasLiveSummariesRef.current = liveSummaries != null;

  const useLive = isAuthenticated && !isSampleMode && !!tokens?.accessToken;
  const spendPeriodKey = homeSpendPeriodKey(spendPeriod);

  const setSpendPeriod = (period: HomeSpendPeriod) => {
    setUserPickedSpendPeriod(true);
    setSpendPeriodState(period);
  };

  // Guest/fixture: Home spend snapshot uses asOfMonth; keep period chip + Spend deep-link in sync.
  useEffect(() => {
    if (useLive || userPickedSpendPeriod) return;
    const asOf = core.profile.asOfMonth;
    if (!/^\d{4}-\d{2}$/.test(asOf)) return;
    setSpendPeriodState((prev) =>
      prev === currentMonthKey() && asOf < prev ? asOf : prev,
    );
  }, [useLive, userPickedSpendPeriod, core.profile.asOfMonth]);

  useEffect(() => {
    const applyGeneration = (generation: number) => {
      if (generation === seenGenerationRef.current) return;
      seenGenerationRef.current = generation;
      setTick((v) => v + 1);
      void refreshHousehold();
    };

    applyGeneration(getDashboardRefreshGeneration());
    return subscribeDashboardRefresh(applyGeneration);
  }, [refreshHousehold]);

  // Signed-in Home follows Mongo. Do not copy onboarding draft assets onto
  // the sheet — that overlay survived a DB wipe and looked like live data.

  // Drop leftover local/sample assets once Mongo portfolio exists.
  useEffect(() => {
    if (!useLive || !hasSyncedSnapshot) return;

    void setCore((prev) => {
      const nextAccounts = localLiabilitiesOnly(prev.accounts);
      if (
        nextAccounts.length === prev.accounts.length &&
        nextAccounts.every((a, i) => a.id === prev.accounts[i]?.id)
      ) {
        return prev;
      }
      return {
        ...prev,
        accounts: nextAccounts,
        // Drop observations for removed local assets; loan planner can re-seed.
        balanceObservations: prev.balanceObservations.filter((obs) =>
          nextAccounts.some((a) => a.id === obs.accountId),
        ),
      };
    });
  }, [useLive, hasSyncedSnapshot, setCore]);

  useEffect(() => {
    if (!useLive || !tokens?.accessToken || !user?.id || !viewReady) {
      if (!useLive || !tokens?.accessToken || !user?.id) {
        setLiveSummaries(null);
        setError(null);
        fallbackAttemptedRef.current = false;
      }
      return;
    }

    let cancelled = false;
    setError(null);

    const periodChanged = loadedPeriodKeyRef.current !== spendPeriodKey;
    if (periodChanged || !hasLiveSummariesRef.current) {
      if (periodChanged) {
        setLiveSummaries(null);
        loadedPeriodKeyRef.current = null;
      }
      setLoading(true);
    }

    const forceRefresh = true;

    void loadAnalyticsSummary(
      {
        accessToken: tokens.accessToken,
        userId: user.id,
        period: 'month',
        periodKey: spendPeriodKey,
        view: householdView,
        forceRefresh,
      },
      (response) => {
        if (cancelled) return;
        const categories = mapCategories(response.spend.categories);
        const totalSpend = Math.abs(response.spend.totalSpend);
        const isEmpty = totalSpend === 0 && categories.length === 0;
        loadedPeriodKeyRef.current = spendPeriodKey;
        setLiveSummaries({
          totalSpend,
          totalSaved: Math.max(0, response.netCashFlow),
          categories,
          netCashFlow: response.netCashFlow,
          isEmpty,
        });
        setLoading(false);

        // Empty selected month → jump to the latest month that has spend (unless user picked).
        if (
          isEmpty &&
          !userPickedSpendPeriod &&
          !fallbackAttemptedRef.current
        ) {
          fallbackAttemptedRef.current = true;
          void getAnalyticsTrends({
            accessToken: tokens.accessToken,
            period: 'month',
            tab: 'spend',
            limit: 6,
            forceRefresh,
          })
            .then((trends) => {
              if (cancelled) return;
              const latest = latestMonthWithSpend(trends.points);
              if (latest && latest !== spendPeriodKey) {
                setSpendPeriodState(latest);
              }
            })
            .catch(() => {
              // Keep empty month — card will still show debt/investing split.
            });
        }
      },
    )
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        if (!hasLiveSummariesRef.current) {
          setLiveSummaries(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    useLive,
    tokens?.accessToken,
    user?.id,
    tick,
    spendPeriodKey,
    userPickedSpendPeriod,
    householdView,
    viewReady,
  ]);

  return useMemo(() => {
    const refresh = () => {
      setTick((v) => v + 1);
      notifyDashboardRefresh('home_manual');
      void refreshHousehold();
      void refreshPortfolio();
      void refreshLiabilities();
    };

    if (!useLive) {
      const usingDraftPreview =
        draftHasMeaningfulAssets(draft) || draftHasCustomIncome(draft);
      const netWorth = mergeHomeLoanIntoNetWorth(
        netWorthFromDraft(draft, derived.netWorth),
        pickPrimaryMortgage(derived.netWorth),
      );
      const profile: SampleProfile = {
        ...derived.profile,
        monthlyIncome: draftHasCustomIncome(draft)
          ? draft.monthlyIncomeAfterTax
          : derived.profile.monthlyIncome,
        annualSalary: draftHasCustomIncome(draft)
          ? draft.monthlyIncomeAfterTax * 12
          : derived.profile.annualSalary,
      };

      return {
        isLive: false,
        loading: false,
        error: null,
        profile,
        summaries: {
          totalSpend: derived.summaries.totalSpend,
          totalSaved: derived.summaries.totalSaved,
          categories: derived.summaries.categories,
          netCashFlow: derived.summaries.totalSaved - derived.summaries.totalSpend,
          isEmpty: false,
        },
        spendPeriod,
        spendPeriodKey,
        setSpendPeriod,
        netWorth,
        mortgage: pickPrimaryMortgage(netWorth),
        projections: derived.projections,
        usingDraftPreview,
        refresh,
      };
    }

    const profile = buildLiveProfile(user?.name);
    const storedIncome = user?.meta?.monthlyIncome;
    if (typeof storedIncome === 'number' && storedIncome > 0) {
      profile.monthlyIncome = storedIncome;
      profile.annualSalary = storedIncome * 12;
    }

    const usingDraftBalanceSheet = false;
    const usingUserAccounts = hasUserOwnedAccounts(core.accounts);
    const hasHomeLoanLiability =
      syncedLiabilities.some((a) => a.type === 'mortgage' && a.balance > 0) ||
      core.accounts.some(
        (a) => a.kind === 'liability' && a.type === 'mortgage' && a.balance > 0,
      );
    const useHouseholdBalanceSheet =
      usingUserAccounts || hasHomeLoanLiability || hasSyncedLiabilities;
    // Liabilities: prefer household-synced rows (Mine/Household view-aware).
    // Assets: Mongo portfolio when synced; else local/draft.
    const liabilityRows = hasSyncedLiabilities
      ? syncedLiabilities
      : localLiabilitiesOnly(core.accounts);
    // First login / import can sync portfolio before liabilities. Keep a
    // locally planned home loan (not bundled sample ids) on the sheet.
    const localHomeLoan = core.accounts.find(
      (account) =>
        account.kind === 'liability' &&
        account.type === 'mortgage' &&
        account.balance > 0 &&
        !isSampleAccountId(account.id),
    );
    const mergedLiabilityRows =
      localHomeLoan &&
      !liabilityRows.some(
        (account) => account.type === 'mortgage' || account.id === localHomeLoan.id,
      )
        ? [...liabilityRows, localHomeLoan]
        : liabilityRows;
    const baseHousehold = hasSyncedSnapshot
      ? { ...core, accounts: mergedLiabilityRows }
      : {
          ...core,
          accounts: [
            ...stripSampleAccounts(core.accounts).filter((a) => a.kind !== 'liability'),
            ...mergedLiabilityRows,
          ],
        };
    const mergedHousehold = usingDraftBalanceSheet
      ? mergeDraftIntoHousehold(draft, baseHousehold)
      : baseHousehold;
    // Synced Mongo portfolio drives assets for net worth + Investments-vs-debt.
    const householdForDerive = overlaySyncedPortfolioOnHousehold(
      mergedHousehold,
      syncedPortfolio,
      hasSyncedSnapshot,
    );
    const mergedDerived =
      usingDraftBalanceSheet || useHouseholdBalanceSheet || hasSyncedSnapshot
        ? deriveHousehold(householdForDerive)
        : null;
    const computedNetWorth = mergedDerived
      ? applySyncedPortfolio(
          mergedDerived.netWorth,
          syncedPortfolio,
          hasSyncedSnapshot,
        )
      : emptyNetWorth();
    const householdMortgage = pickPrimaryMortgage(
      mergedDerived?.netWorth ?? derived.netWorth,
    );
    const netWorth = mergeHomeLoanIntoNetWorth(
      computedNetWorth,
      householdMortgage && !isSampleAccountId(householdMortgage.id)
        ? householdMortgage
        : null,
    );
    const projections = mergedDerived
      ? mergedDerived.projections
      : emptyProjections();

    return {
      isLive: true,
      loading,
      error,
      profile,
      summaries: liveSummaries ?? {
        totalSpend: 0,
        totalSaved: 0,
        categories: [],
        netCashFlow: 0,
        isEmpty: true,
      },
      spendPeriod,
      spendPeriodKey,
      setSpendPeriod,
      netWorth,
      mortgage: pickPrimaryMortgage(netWorth),
      projections,
      usingDraftPreview: usingDraftBalanceSheet && !usingUserAccounts,
      refresh,
    };
  }, [
    useLive,
    draft,
    derived,
    core,
    syncedPortfolio,
    hasSyncedSnapshot,
    liveSummaries,
    loading,
    error,
    spendPeriod,
    spendPeriodKey,
    userPickedSpendPeriod,
    user?.name,
    user?.meta?.monthlyIncome,
    refreshHousehold,
    refreshPortfolio,
    refreshLiabilities,
    syncedLiabilities,
    hasSyncedLiabilities,
  ]);
}

export function useSpendDashboard(): HomeDashboardData {
  return useHomeDashboard();
}
